/**
 * Mobile-only Push Notification Manager
 *
 * Handles service worker registration on mobile app load.
 * The actual toggle UI is in SettingsDialog -> Notifications tab.
 * 
 * This component can optionally show a toggle button if showToggle is true.
 */

import { useEffect } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';

interface PushNotificationManagerProps {
  /** Show toggle button UI (default: false, just registers service worker) */
  showToggle?: boolean;
}

export function PushNotificationManager({ showToggle = false }: PushNotificationManagerProps) {
  const push = usePushNotifications();

  // Toggle push notification subscription
  const handleToggle = async () => {
    if (push.isSubscribed) {
      await push.unsubscribe();
    } else {
      await push.subscribe();
    }
  };

  // Auto-register service worker on mount
  useEffect(() => {
    if (push.isSupported && push.isConfigured) {
      push.registerServiceWorker();
    }
  }, [push.isSupported, push.isConfigured]); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't render anything if not showing toggle
  if (!showToggle) {
    return null;
  }

  // Not supported or not configured
  if (!push.isSupported || !push.isConfigured) {
    return null;
  }

  // Permission denied
  if (push.permissionState === 'denied') {
    return (
      <Button variant="ghost" size="sm" disabled className="gap-2 opacity-50">
        <BellOff className="h-4 w-4" />
        <span className="text-xs">Blocked</span>
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleToggle}
      disabled={push.isLoading}
      className="gap-2"
    >
      {push.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : push.isSubscribed ? (
        <Bell className="h-4 w-4 text-success" />
      ) : (
        <BellOff className="h-4 w-4 text-muted-foreground" />
      )}
    </Button>
  );
}

export default PushNotificationManager;
